// Malaysia is UTC+8 with no DST — safe to hardcode
const MYT_OFFSET_MS = 8 * 60 * 60 * 1000
const SOON_THRESHOLD_MS = 60 * 60 * 1000 // 60 minutes
const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const

interface Period {
  open: { day: string; time: string }
  close: { day: string; time: string }
}

export interface ComputedOpeningStatus {
  isOpen: boolean
  statusKey: string
  statusLabel: string
  statusTone: "open" | "soon" | "closed" | "neutral"
  transitionText: string | null
  displayText: string
}

// Returns a Date whose UTC accessors (getUTCDay, getUTCHours, etc.) give MYT wall-clock values
function getMYTNow(): Date {
  return new Date(Date.now() + MYT_OFFSET_MS)
}

// Build a Date in the MYT wall-clock frame from year/month/day/hour/minute (all MYT)
function mytDate(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0
): Date {
  return new Date(Date.UTC(year, month, day, hour, minute, 0, 0))
}

function parseHHMM(timeStr: string): [number, number] {
  const [h, m] = timeStr.split(":").map(Number)
  return [h ?? 0, m ?? 0]
}

function compactTime(hour: number, minute: number): string {
  const period = hour >= 12 ? "PM" : "AM"
  const h12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
  return minute === 0
    ? `${h12} ${period}`
    : `${h12}:${String(minute).padStart(2, "0")} ${period}`
}

function isSameMYTDate(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  )
}

function shortDayName(date: Date): string {
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getUTCDay()]
}

interface Slot {
  openAt: Date
  closeAt: Date
}

function buildSlots(periods: Period[], now: Date): Slot[] {
  const slots: Slot[] = []

  for (const period of periods) {
    const startDayIdx = DAYS.indexOf(period.open.day as (typeof DAYS)[number])
    const closeDayIdx = DAYS.indexOf(period.close.day as (typeof DAYS)[number])
    if (startDayIdx === -1 || closeDayIdx === -1) continue

    const [openHour, openMin] = parseHHMM(period.open.time)
    const [closeHour, closeMin] = parseHHMM(period.close.time)
    const closeDayOffset = (closeDayIdx - startDayIdx + 7) % 7

    for (let offset = -7; offset <= 7; offset++) {
      const baseDate = mytDate(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + offset
      )
      if (baseDate.getUTCDay() !== startDayIdx) continue

      const openAt = mytDate(
        baseDate.getUTCFullYear(),
        baseDate.getUTCMonth(),
        baseDate.getUTCDate(),
        openHour,
        openMin
      )

      let closeAt = mytDate(
        baseDate.getUTCFullYear(),
        baseDate.getUTCMonth(),
        baseDate.getUTCDate() + closeDayOffset,
        closeHour,
        closeMin
      )

      // Handle midnight (0:00) and other wraparound cases
      if (closeAt <= openAt) {
        closeAt = new Date(closeAt.getTime() + 24 * 60 * 60 * 1000)
      }

      if (closeAt <= now) continue
      if (openAt > new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) continue

      slots.push({ openAt, closeAt })
    }
  }

  return slots
}

export function computeOpeningStatus(periods: Period[]): ComputedOpeningStatus {
  if (periods.length === 0) {
    return {
      isOpen: false,
      statusKey: "no_hours",
      statusLabel: "No opening hours",
      statusTone: "neutral",
      transitionText: null,
      displayText: "No opening hours",
    }
  }

  const now = getMYTNow()
  const slots = buildSlots(periods, now)

  const currentSlot = slots
    .filter((s) => s.openAt <= now && now < s.closeAt)
    .sort((a, b) => a.closeAt.getTime() - b.closeAt.getTime())[0]

  if (currentSlot) {
    if (
      currentSlot.closeAt.getTime() - currentSlot.openAt.getTime() >=
      24 * 60 * 60 * 1000
    ) {
      return {
        isOpen: true,
        statusKey: "open_24_hours",
        statusLabel: "Open 24 hours",
        statusTone: "open",
        transitionText: null,
        displayText: "Open 24 hours",
      }
    }

    const closesSoon =
      currentSlot.closeAt.getTime() - now.getTime() <= SOON_THRESHOLD_MS
    const statusKey = closesSoon ? "closes_soon" : "open"
    const statusLabel = closesSoon ? "Closes soon" : "Open"
    const statusTone: "open" | "soon" = closesSoon ? "soon" : "open"
    const closeFmt = compactTime(
      currentSlot.closeAt.getUTCHours(),
      currentSlot.closeAt.getUTCMinutes()
    )
    const closeDay = isSameMYTDate(currentSlot.closeAt, now)
      ? null
      : shortDayName(currentSlot.closeAt)
    const transitionText = closesSoon
      ? [closeFmt, closeDay].filter(Boolean).join(" ")
      : `Closes ${[closeFmt, closeDay].filter(Boolean).join(" ")}`

    return {
      isOpen: true,
      statusKey,
      statusLabel,
      statusTone,
      transitionText,
      displayText: `${statusLabel} · ${transitionText}`,
    }
  }

  const nextSlot = slots
    .filter((s) => s.openAt > now)
    .sort((a, b) => a.openAt.getTime() - b.openAt.getTime())[0]

  if (nextSlot) {
    const opensToday = isSameMYTDate(nextSlot.openAt, now)
    const opensSoon =
      nextSlot.openAt.getTime() - now.getTime() <= SOON_THRESHOLD_MS
    const statusKey = opensSoon
      ? "opens_soon"
      : opensToday
        ? "closed_opens_today"
        : "closed_opens_future"
    const statusLabel = opensSoon ? "Opens soon" : "Closed"
    const statusTone: "soon" | "closed" = opensSoon ? "soon" : "closed"
    const openFmt = compactTime(
      nextSlot.openAt.getUTCHours(),
      nextSlot.openAt.getUTCMinutes()
    )
    const openDay = isSameMYTDate(nextSlot.openAt, now)
      ? null
      : shortDayName(nextSlot.openAt)
    const transitionText = opensSoon
      ? [openFmt, openDay].filter(Boolean).join(" ")
      : ["Opens", openFmt, openDay].filter(Boolean).join(" ")

    return {
      isOpen: false,
      statusKey,
      statusLabel,
      statusTone,
      transitionText,
      displayText: `${statusLabel} · ${transitionText}`,
    }
  }

  return {
    isOpen: false,
    statusKey: "closed_no_future",
    statusLabel: "Closed",
    statusTone: "neutral",
    transitionText: null,
    displayText: "Closed",
  }
}
